import { AutocompleteOption, ControlledAutocomplete } from '@/components/inputs'
import { useTranslation } from 'react-i18next'
import { ControlledFieldProps, Equipment, EventResearcher } from '@/types'
import { FieldValues } from 'react-hook-form/dist/types/fields'
import { useAvailableResearchers } from '@/hooks/api/useAvailableResearchers'
import { Dayjs } from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useWatch } from 'react-hook-form'
import {
  faBicycle,
  faCamera,
  faMicrophone,
  faCar,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const getExperienceLabel = (isSenior?: boolean) => (isSenior ? 'S' : 'J')

const getEquipmentIcon = (equipment: Equipment) => {
  if (equipment.code === 'bat_logger') {
    return (
      <FontAwesomeIcon style={{ paddingLeft: '5px' }} icon={faMicrophone} />
    )
  } else if (equipment.code === 'bicycle') {
    return <FontAwesomeIcon style={{ paddingLeft: '5px' }} icon={faBicycle} />
  } else if (equipment.code === 'night_camera') {
    return <FontAwesomeIcon style={{ paddingLeft: '5px' }} icon={faCamera} />
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        border: '1px solid black',
        borderRadius: '50%',
        fontSize: 10,
        fontWeight: 700,
        ml: 1,
      }}
    >
      {equipment.short_code}
    </Box>
  )
}

export const getResearcherOptionLabel = ({
  id,
  first_name: name,
  last_name: surname,
  is_senior: isSenior,
  is_contract_active: isContractActive,
  equipment,
  distance,
  allowed_number_of_research_areas,
  max_number_of_research_areas,
  has_driver_license: hasDriverLicense,
}: EventResearcher): AutocompleteOption => ({
  value: id.toString(),
  title: `${name} ${surname}${
    allowed_number_of_research_areas !== null &&
    allowed_number_of_research_areas !== undefined &&
    max_number_of_research_areas !== null &&
    max_number_of_research_areas !== undefined
      ? ` - ${allowed_number_of_research_areas} / ${max_number_of_research_areas} areas`
      : allowed_number_of_research_areas !== null &&
        allowed_number_of_research_areas !== undefined
      ? ` - ${allowed_number_of_research_areas} areas`
      : ''
  }${distance ? ` - ${distance} km` : ''}`,
  isSenior,
  isContractActive,
  hasDriverLicense,
  equipment: equipment ?? null,
})

export type ResearchersControlledAutocompleteProps<T extends FieldValues> = {
  eventDate?: Dayjs | null | string
  eventEndDate?: Dayjs | null | string
  protocolIds?: number[] | string[]
  researchAreaId?: number
  eventId?: number
  latitude?: string
  longitude?: string
  onChange?: (item: AutocompleteOption[]) => void
  requiredEquipment?: Equipment[]
} & Pick<ControlledFieldProps<T>, 'name' | 'control'>

export const ResearchersControlledAutocomplete = <T extends FieldValues>({
  name,
  control,
  eventDate,
  eventEndDate,
  protocolIds,
  researchAreaId,
  eventId,
  latitude,
  longitude,
  onChange,
  requiredEquipment,
}: ResearchersControlledAutocompleteProps<T>): JSX.Element => {
  const { t } = useTranslation('forms')

  const notAvailableEmployees = useRef<string[]>([])
  const [options, setOptions] = useState<AutocompleteOption[]>([])

  const { data: employees, isLoading } = useAvailableResearchers({
    date: eventDate,
    endDate: eventEndDate,
    protocolIds,
    researchAreaId,
    eventId,
    latitude,
    longitude,
  })

  useEffect(() => {
    if (!employees?.length) {
      return
    }

    notAvailableEmployees.current = []

    const sorted = employees
      .filter((e) => e.is_active)
      .sort((a, b) => a.distance - b.distance)

    const mapped = sorted.map((researcher) => {
      const { id, is_available: isAvailable } = researcher

      if (!isAvailable) {
        notAvailableEmployees.current.push(id.toString())
      }

      return getResearcherOptionLabel(researcher)
    })

    setOptions(mapped)
  }, [employees])

  const selectedOptions = useWatch({
    control,
    name,
  })

  const selectedResearchersWithInactiveContract =
    selectedOptions?.filter(
      (o: AutocompleteOption) => o.isContractActive === false
    ) ?? []

  const selectedResearchersWithoutDriverLicense =
    selectedOptions?.filter(
      (o: AutocompleteOption) => o.hasDriverLicense === false
    ) ?? []

  const noResearchersWithDriverLicense =
    Array.isArray(selectedOptions) &&
    selectedOptions.length > 0 &&
    selectedResearchersWithoutDriverLicense.length === selectedOptions.length

  const getResearcherName = (id: string) => {
    const researcher = employees?.find((e) => e.id === +id)

    if (!researcher) {
      return ''
    }

    return `${researcher.first_name} ${researcher.last_name}`
  }

  return (
    <div>
      <ControlledAutocomplete
        name={name}
        control={control}
        options={options}
        multiple={true}
        filterSelectedOptions={true}
        textFieldProps={{
          placeholder: t('placeholders.pickResearchers'),
          label: t('labels.researchers'),
          fullWidth: true,
        }}
        getOptionDisabled={(option) => {
          const disabledByAvailability = notAvailableEmployees.current.includes(
            option.value
          )

          const reqIds = (requiredEquipment ?? []).map((e) => e.id)
          const researcherEqIds = (
            (option?.equipment as Equipment[] | null) ?? []
          ).map((e) => e.id)

          const disabledByEquipment = reqIds.length
            ? !reqIds.every((id) => researcherEqIds.includes(id))
            : false

          return disabledByAvailability || disabledByEquipment
        }}
        loading={isLoading}
        renderOption={(props, option) => (
          <li {...props} key={option.value + option.title}>
            <Box
              component="span"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              fontSize={12}
              width={22}
              height={22}
              p={2}
              mr={2}
              bgcolor={option?.isSenior ? 'green.500' : 'green.100'}
              borderRadius="50%"
              fontWeight={700}
            >
              {getExperienceLabel(option?.isSenior as boolean)}
            </Box>
            {option.title}
            {(option?.equipment as Equipment[])?.map((eq) =>
              getEquipmentIcon(eq)
            )}
            {option.hasDriverLicense && (
              <FontAwesomeIcon
                icon={faCar}
                style={{ color: '#2e7d32', marginLeft: 6 }}
              />
            )}
          </li>
        )}
        getOptionLabel={(option) =>
          typeof option === 'string'
            ? option
            : `(${getExperienceLabel(option?.isSenior as boolean)}) ${
                option.title
              }`
        }
        onChangeCustom={(value) => {
          onChange && onChange(value as AutocompleteOption[])
        }}
        data-testid="researchers-autocomplete"
      />
      {!!selectedResearchersWithInactiveContract.length && (
        <Typography color="warning.dark">
          {t('projectArea.notification.researchersWithInactiveContract', {
            researchers: selectedResearchersWithInactiveContract
              .map((o: AutocompleteOption) => getResearcherName(o.value))
              .join(', '),
          })}
        </Typography>
      )}
      {noResearchersWithDriverLicense && (
        <Typography color="warning.dark">
          {t('projectArea.notification.noResearchersWithDriverLicense')}
        </Typography>
      )}
    </div>
  )
}
